import { PeopleTableInfo } from '@aibees/elephant/feature/search-table-people';
import { CustomPopover, usePopover } from '@aibees/shared/components';
import { usePopoverPosition } from '@aibees/shared/hooks';
import { useTableSearchItemStore } from '@aibees/shared/store/table-search-item';
import { classnames } from '@aibees/shared/utils';
import { memo, useCallback, useState } from 'react';
import {
  QUICK_INFO_BUTTONS,
  QuickInfoButton,
  QuickInfoButtonProps,
} from './components/quick-info-button';
import { useGetPeopleQuickInfoPopoverContent } from './hooks/use-get-people-quick-info-popover-content';
import * as Styled from './styles';
import * as SX from './sx';
import type { QuickInfoGroupProps as Props } from './types';

const POPOVER_POSITION_OPTIONS = { thresholdPercentage: 50 };

export const QuickInfoGroup = memo(
  ({ className, items, data }: Props<PeopleTableInfo>) => {
    const [selectedId, setSelectedId] = useState<QUICK_INFO_BUTTONS>();
    const { setSelectedItem } = useTableSearchItemStore();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { anchorOrigin, transformOrigin } = usePopoverPosition(
      anchorEl,
      POPOVER_POSITION_OPTIONS
    );
    const popoverState = usePopover();
    const isPopoverOpen = Boolean(popoverState.open);

    const content = useGetPeopleQuickInfoPopoverContent(
      selectedId,
      data,
      isPopoverOpen
    );

    const handleSelect = useCallback<
      QuickInfoButtonProps<PeopleTableInfo>['onSelect']
    >(
      (target, item, id) => {
        setSelectedId(id);
        setSelectedItem(item.id);
        popoverState.onOpen(target);
        setAnchorEl(target);
      },
      [setSelectedItem, popoverState]
    );

    const handleClose = useCallback(() => {
      setSelectedItem(undefined);
    }, [setSelectedItem]);

    return (
      <>
        <Styled.Wrapper
          className={classnames('QuickInfoGroup', className)}
          variant="outlined"
          size="medium"
          color="inherit"
          sx={SX.root}
        >
          {items.map((x) => (
            <QuickInfoButton
              key={x.id}
              {...x}
              data={data}
              onSelect={handleSelect}
            />
          ))}
        </Styled.Wrapper>

        <CustomPopover
          state={popoverState}
          anchorEl={anchorEl}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          sx={SX.popover}
          contentSx={SX.popoverContent}
          padding="none"
          onClose={handleClose}
        >
          {content}
        </CustomPopover>
      </>
    );
  }
);

QuickInfoGroup.displayName = 'QuickInfoGroup';
